"use client";

import { curveLinear } from "@visx/curve";
import { AreaClosed, LinePath } from "@visx/shape";
import { motion } from "framer-motion";
import React, { useId } from "react";
import { useChartContext } from "../../../context/ChartContext";
import { BaseDataPoint } from "../../../utils/charts";

interface AreaSeriesProps {
  data: BaseDataPoint[];
  color?: string;
  label?: string;
  categoryColors?: Record<string, string>;
  categoryLabels?: Record<string, string>;
}

/**
 * AreaSeries
 *
 * Chart component for rendering area charts with gradient fill.
 * Displays data as a filled area with optional line path, supporting animations and custom colors.
 */
export const AreaSeries: React.FC<AreaSeriesProps> = ({ data, color = "var(--color-viz-default)" }) => {
  const uniqueGradientId = useId();
  const clipPathId = useId();
  const { xScale, yScale, animationSettings } = useChartContext();

  // Get the exact plotting area bounds from the scales
  const xRange = xScale.range();
  const yRange = yScale.range();

  return (
    <>
      <defs>
        <linearGradient id={uniqueGradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="50%" stopColor={color} stopOpacity={0.1} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
        <clipPath id={clipPathId}>
          <rect x={xRange[0]} y={yRange[1]} width={xRange[1] - xRange[0]} height={yRange[0] - yRange[1]} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipPathId})`}>
        <motion.g
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{
            duration: animationSettings.duration,
            ease: animationSettings.ease as "easeInOut",
          }}
          style={{
            transformOrigin: `0 ${yRange[0]}px`,
            transformBox: "fill-box",
            isolation: "isolate",
          }}
        >
          <AreaClosed
            data={data}
            x={(d) => xScale(d.xValue)}
            y={(d) => yScale(d.yValue)}
            yScale={yScale}
            fill={`url(#${uniqueGradientId})`}
            curve={curveLinear}
          />
          <LinePath
            data={data}
            x={(d) => xScale(d.xValue)}
            y={(d) => yScale(d.yValue)}
            stroke={color}
            strokeWidth={3}
            curve={curveLinear}
          />
        </motion.g>
      </g>
    </>
  );
};
